import React, { useState } from "react";
import { createPortal } from "react-dom";
import { socialReels } from "../blogData";
import Footer from "../components/heider/Footer";
import ContactForm from "../components/heider/ContactForm";
import BrokerFilms from "../components/heider/BrokerFilms";
import useReveal from "../hooks/useReveal";
import { Play, X, ArrowRight } from "lucide-react";

const BrokerOpens = () => {
  const [active, setActive] = useState(null);
  useReveal();

  return (
    <div className="page-in">
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline poster="">
          <source src="/broker-mclean.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white px-6 max-w-4xl">
          <p className="text-[11px] tracking-luxe uppercase text-[#e7c9a3] mb-5 fade-in" style={{ animationDelay: "0.2s", opacity: 0 }}>Cinematic Listing Marketing</p>
          <h1 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl leading-[1.05]">No One Does <span className="italic text-[#e7c9a3]">Broker Opens</span> Like Us</h1>
          <p className="mt-6 text-[13px] md:text-sm tracking-luxe-sm text-white/80 max-w-2xl mx-auto">We turn every listing into a produced event — cinematic film, sound design, and a room full of the DMV's most active agents before the public ever sees it.</p>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-6">The First Showing Begins Online</p>
          <p className="font-serif-display text-2xl md:text-[2rem] leading-relaxed text-[#2a2a2a] font-light">
            A broker open is not a party — it is a distribution strategy. Our films are built to stop the scroll, and our open houses put your home in front of buyers who are ready to move.
          </p>
        </div>

        {/* Video showcase */}
        <BrokerFilms />
      </section>

      {/* Social Reels */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">From the Feed</p>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a]">Social Reels & Content</h2>
            <p className="text-neutral-500 text-[15px] leading-relaxed max-w-2xl mx-auto mt-5">The same production quality, cut for the platforms where DMV buyers actually spend their time.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {socialReels.map((v, i) => (
              <button
                key={i}
                data-testid={`social-reel-${i}`}
                onClick={() => setActive(v)}
                className="group relative overflow-hidden bg-black rounded-[1.6rem] aspect-[9/16] reveal-scale"
              >
                <video className="w-full h-full object-cover" autoPlay loop muted playsInline preload="metadata">
                  <source src={v} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full bg-[#b4936a]/85 text-white flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={18} className="ml-0.5" /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0d0d0d] text-white py-24 md:py-32 text-center">
        <div className="max-w-[800px] mx-auto px-6 reveal">
          <h2 className="font-serif-display text-4xl md:text-5xl font-light mb-6">Ready to Market Your Home Like This?</h2>
          <p className="text-white/55 leading-relaxed mb-8">Let's create the film and the event that gets your listing the attention it deserves across the DMV.</p>
          <a href="#contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase border border-white px-8 py-4 hover:bg-white hover:text-[#0d0d0d] transition-colors">Schedule a Consultation <ArrowRight size={14} /></a>
        </div>
      </section>

      {active && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="broker-lightbox">
          <button className="fixed top-4 right-4 z-[110] w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/40 text-white flex items-center justify-center active:scale-90 hover:bg-[#b4936a] transition-all" aria-label="Close video" data-testid="close-broker-video" onClick={(e) => { e.stopPropagation(); setActive(null); }}>
            <X size={26} strokeWidth={1.6} />
          </button>
          <video key={active} className="max-h-[80vh] w-auto max-w-[92vw] rounded-xl shadow-2xl" controls autoPlay playsInline onClick={(e) => e.stopPropagation()}>
            <source src={active} type="video/mp4" />
          </video>
          <button className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[110] text-[11px] tracking-luxe uppercase text-white/70 border border-white/25 rounded-full px-6 py-2.5 hover:text-white hover:border-white transition-all" onClick={(e) => { e.stopPropagation(); setActive(null); }}>Close</button>
        </div>,
        document.body
      )}

      <ContactForm />
      <Footer />
    </div>
  );
};

export default BrokerOpens;
